import {
  useLazyGetCasesFiltersQuery,
  useLazyGetCasesQuery,
  CasesFiltersUser,
  CasesItem,
  casesItemFieldMap,
  CasesFiltersUrgentOptions,
  CreatedByInfo,
  AssignedToInfo,
  CaseStatusOption,
} from '@/api';
import { AccessControlAction, CASE_PROCESSING_STATUSES, CASE_FAILED_STATUSES } from '@/constants';
import { useAccessControl } from '@/hooks';
import {
  selectCasesFilters,
  selectCasesFinding,
  selectCasesList,
  selectCasesPagination,
  selectCasesRowCount,
  selectCasesSorting,
  selectSelectedCasesFilters,
  setCaseIdData,
  setCasesPagination,
  setCasesSorting,
  setSelectedCasesFilters,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import {
  DataTableProgressBarRow,
  PencilIcon,
  UserAvatar,
  ProgressSpinner,
  YellowWarningIcon,
} from '@repo/atomic-components';
import { ColumnDef, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { format } from 'date-fns';
import { classNames } from 'primereact/utils';
import { useCallback, useEffect, useMemo } from 'react';

export interface UploadDocumentsTableRow extends CasesItem {
  progressBars?: DataTableProgressBarRow[];
}

/**
 * Default case statuses used for the initial cases request before the
 * filters endpoint has responded. Mirrors the non-Closed statuses the
 * backend returns so the first request excludes Closed cases without
 * waiting for the filter endpoint.
 */
const DEFAULT_CASE_STATUSES = [
  'NEW',
  'CLONING',
  'CLONING_FAILED',
  'DOCUMENT_UPLOAD',
  'PREPROCESSING_ERROR',
  'READY_FOR_AI_PROCESSING',
  'AI_PROCESSING_REQUESTED',
  'CONFIGURING_AI_PIPELINE',
  'AI_PAGINATION_PROCESSING',
  'AI_PAGINATION_FAILED',
  'AI_PAGINATION_COMPLETE',
  'MEDICAL_MATCH_PROCESSING',
  'MEDICAL_MATCH_COMPLETE',
  'READY_FOR_REVIEW',
];

interface useCasesListTableProps {
  setIsCreateEditCaseSidebarVisible: (isVisible: boolean) => void;
}

export const useCasesListTable = ({ setIsCreateEditCaseSidebarVisible }: useCasesListTableProps) => {
  const dispatch = useAppDispatch();

  const casesList = useAppSelector(selectCasesList);
  const casesRowCount = useAppSelector(selectCasesRowCount);
  const casesPagination = useAppSelector(selectCasesPagination);
  const casesSorting = useAppSelector(selectCasesSorting);
  const casesFilters = useAppSelector(selectCasesFilters);
  const selectedCasesFilters = useAppSelector(selectSelectedCasesFilters);
  const casesFinding = useAppSelector(selectCasesFinding);

  const isCreateEditCase = useAccessControl(AccessControlAction.CREATE_EDIT_CASE);

  // No local row edit state, all changes go to API
  // const [data, setData] = useState<UploadDocumentsTableRow[]>([]);

  const [triggerCases, { isFetching: getCasesFetching }] = useLazyGetCasesQuery();
  const [triggerCasesFilters] = useLazyGetCasesFiltersQuery();

  useEffect(() => {
    triggerCasesFilters({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Refetch data when pagination, filters or sorting change.
   * Fires immediately in parallel with the filter options fetch
   * using DEFAULT_CASE_STATUSES as the initial status filter.
   * Once the filter endpoint responds and sets selectedCasesFilters,
   * this effect reruns with the API-provided status values.
   */
  useEffect(() => {
    let sortBy: string | undefined = undefined;
    let sortDirection: string | undefined = undefined;
    if (casesSorting && casesSorting.length > 0 && casesSorting[0].id in casesItemFieldMap) {
      sortBy = casesItemFieldMap[casesSorting[0].id as keyof typeof casesItemFieldMap];
      sortDirection = casesSorting[0].desc ? 'descending' : 'ascending';
    }

    triggerCases(
      {
        pageIndex: casesPagination.pageIndex,
        pageSize: casesPagination.pageSize,
        sortBy,
        sortDirection,
        assignedTo: selectedCasesFilters.assignedTo
          ? (selectedCasesFilters.assignedTo as CasesFiltersUser[]).map((u) => (u ? u.userId : 'null'))
          : [],
        createdBy: selectedCasesFilters.createdBy
          ? (selectedCasesFilters.createdBy as CasesFiltersUser[]).map((u) => u.userId)
          : [],
        status: selectedCasesFilters.status.length
          ? selectedCasesFilters.status.map((s) => s.raw)
          : casesFilters.status.length === 0
            ? DEFAULT_CASE_STATUSES
            : [],
        isUrgent:
          // Return 'true' / 'false' if only 1 element selected
          selectedCasesFilters.isUrgent.length === 1
            ? selectedCasesFilters.isUrgent.includes(CasesFiltersUrgentOptions.YES)
            : undefined,
        caseReference: casesFinding.length ? casesFinding : undefined,
      },
      false,
    );
    // casesFilters is intentionally excluded — when it updates (filters load),
    // selectedCasesFilters also updates in the same render (both dispatched in
    // onQueryStarted), so the effect already reruns via that dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casesPagination, casesSorting, selectedCasesFilters, triggerCases, casesFinding]);

  // Shared between the row's pencil icon and the "Unassigned" button on the
  // assignedTo column — both surface the same Edit-Case action and rely on
  // the same `data-edit-case-id` attribute. `dataset` is available on both
  // SVGSVGElement and HTMLButtonElement (via HTMLOrSVGElement), so the
  // union type lets one callback serve both call sites.
  const handleEditCaseClick = useCallback(
    (event: React.MouseEvent<SVGSVGElement | HTMLButtonElement>) => {
      const editCaseId = event.currentTarget.dataset.editCaseId;

      if (editCaseId && setIsCreateEditCaseSidebarVisible) {
        dispatch(setCaseIdData(editCaseId));
        setIsCreateEditCaseSidebarVisible(true);
      }
      event.stopPropagation();
    },
    [dispatch, setIsCreateEditCaseSidebarVisible],
  );

  // Adapt filter options for DataTableHeaderFilter. The urgent filter no
  // longer lives on a column header — it's surfaced via the "Urgent" quick-
  // filter chip in the ActionsPanel above the table. The URGENT badge in
  // the caseReference cell remains as a visual indicator only.
  const filterOptions: Record<string, string[]> = {
    assignedTo: casesFilters.assignedTo.map((u: CasesFiltersUser | null) => (u ? u.userName : 'Unassigned')),
    createdBy: casesFilters.createdBy.map((u: CasesFiltersUser) => u.userName),
    status: casesFilters.status.map((s: CaseStatusOption) => s.rendered),
  };

  // Selected filter values for DataTableHeaderFilter
  const selectedFilterOptions: Record<string, string[]> = {
    assignedTo: selectedCasesFilters.assignedTo.map((u: CasesFiltersUser | null) => (u ? u.userName : 'Unassigned')),
    createdBy: selectedCasesFilters.createdBy.map((u: CasesFiltersUser) => u.userName),
    status: selectedCasesFilters.status.map((s: CaseStatusOption) => s.rendered),
  };

  // Convert selectedFilterOptions to ColumnFilter[] for TanStack Table
  const columnFilters = Object.entries(selectedFilterOptions)
    .filter(([, value]) => value.length > 0)
    .map(([id, value]) => ({ id, value }));

  // Handle filter change (called on Apply in DataTableHeaderFilter)
  const handleFilterChange = (columnId: string, values: string[]) => {
    // Update selected filters in store
    const updated = { ...selectedCasesFilters };
    if (columnId === 'assignedTo') {
      updated.assignedTo = casesFilters.assignedTo.filter((u) => values.includes(u ? u.userName : 'Unassigned'));
    } else if (columnId === 'createdBy') {
      updated.createdBy = casesFilters.createdBy.filter((u) => values.includes(u.userName));
    } else if (columnId === 'status') {
      updated.status = casesFilters.status.filter((s) => values.includes(s.rendered));
    }
    dispatch(setSelectedCasesFilters(updated));
    // Reset pagination to first page
    dispatch(setCasesPagination({ ...casesPagination, pageIndex: 0 }));
  };

  const columns = useMemo<ColumnDef<UploadDocumentsTableRow, unknown>[]>(
    () => [
      {
        accessorKey: 'caseReference',
        header: 'Case reference',
        cell: (info) => {
          // caseReference, gRef, and the URGENT badge share this column.
          // gRef sits 8px to the right of the case ref in muted grey as a
          // secondary identifier; the URGENT pill sits after them when the
          // case is flagged urgent. caseReference + gRef truncate at 20
          // chars with an ellipsis; the untruncated value lives on `title`
          // for hover. gRef is hidden when it matches the caseReference
          // exactly, since repeating the same string twice would just be
          // visual noise.
          const ref = info.getValue() as string | undefined;
          const gRef = info.row.original.gRef;
          const showGRef = !!gRef && gRef !== ref;
          const isUrgent = !!info.row.original.isUrgent;
          if (!ref && !showGRef && !isUrgent) return '';
          const truncate = (value: string) => (value.length > 20 ? `${value.slice(0, 20)}...` : value);
          return (
            <div className="flex items-center gap-2 whitespace-nowrap">
              {ref && (
                <span className="font-semibold" title={ref}>
                  {truncate(ref)}
                </span>
              )}
              {showGRef && (
                <span className="text-fletchers-gray-200" title={gRef}>
                  {truncate(gRef)}
                </span>
              )}
              {isUrgent && (
                // 20px tall pastel-red pill with dark-red 12px label — sits
                // inline after the refs so urgent cases are visible without
                // a dedicated column. Uses fletchers-red-50 (pastel background)
                // and fletchers-red-900 (dark text) rather than the main brand
                // red-500 which is too saturated for a chip background.
                <span
                  className="bg-fletchers-red-50 text-fletchers-red-900 inline-flex items-center rounded-[4px] px-2 text-[12px] font-semibold tracking-wide"
                  style={{ height: 20 }}
                >
                  URGENT
                </span>
              )}
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: 'clientName',
        header: 'Client',
        cell: (info) => {
          const value = info.getValue() as string | null | undefined;
          if (!value) return '';
          return (
            <span className="block max-w-[200px] overflow-hidden overflow-ellipsis whitespace-nowrap" title={value}>
              {value}
            </span>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => {
          // Status and its last-updated timestamp share this column — the
          // timestamp sits 8px to the right of the status in muted grey so
          // it reads as a secondary "when" detail rather than a peer field.
          // Matches the caseReference + gRef pattern above visually, but
          // without the 16-char truncation (status labels and timestamps
          // are short and known-bounded, so character-clipping isn't
          // needed here). Sorting still keys off `status` alone, since the
          // datetime is a presentation-only follow-on.
          //
          // Processing statuses show a spinner; failed statuses show a yellow
          // warning icon. Both icons sit 8px to the left of the status text.
          const status = info.getValue() as string | undefined;
          const statusUpdated = info.row.original.statusUpdated;
          const updatedText = statusUpdated ? format(new Date(statusUpdated), 'HH:mm d MMM y') : '';

          const isProcessing =
            status && CASE_PROCESSING_STATUSES.includes(status as (typeof CASE_PROCESSING_STATUSES)[number]);
          const isFailed = status && CASE_FAILED_STATUSES.includes(status as (typeof CASE_FAILED_STATUSES)[number]);

          if (!status && !updatedText) return '';
          return (
            <div className="flex items-center whitespace-nowrap">
              {isProcessing && (
                <div className="mr-2 flex items-center">
                  <ProgressSpinner style={{ width: '16px', height: '16px' }} strokeWidth="4" animationDuration="1.5s" />
                </div>
              )}
              {isFailed && <YellowWarningIcon width={16} height={16} className="mr-2" />}
              {status && <span>{status}</span>}
              {updatedText && <span className="text-fletchers-gray-200 ml-2">{updatedText}</span>}
            </div>
          );
        },
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        accessorKey: 'createdBy',
        header: 'Created by',
        cell: (info) => {
          // A 24px coloured pill carrying the user's initials sits to the
          // left of the name — the hue rotates per the first letter of their
          // name via `getAvatarColorsFromName`. `font-size: 10px` matches the
          // small pill size; gap-2 leaves an 8px breathing space to the name.
          const value = info.getValue() as CreatedByInfo | null | undefined;
          const userName = value?.userName ?? '';
          if (!userName) return '';
          return (
            <div className="flex items-center gap-2">
              <UserAvatar name={userName} size={24} fontSize={10} />
              <span
                className={classNames('block max-w-[300px] overflow-hidden overflow-ellipsis whitespace-nowrap')}
                title={userName}
              >
                {userName}
              </span>
            </div>
          );
        },
        enableColumnFilter: true,
        enableSorting: false,
      },
      {
        accessorKey: 'assignedTo',
        header: 'Assigned to',
        cell: (info) => {
          // Same coloured pill treatment as the Created-by cell, with a
          // dedicated grey-silhouette pill + italic muted "Unassigned" label
          // when the case has no one assigned. Cases can only ever have one
          // assignee so the silhouette is the single-figure variant of the
          // nav-bar Users icon.
          const value = info.getValue() as AssignedToInfo | null | undefined;
          const userName = value?.userName ?? '';
          if (!userName) {
            // Clickable shortcut to the Edit Case sidebar — same action as the
            // row's pencil icon, gated on the same `isCreateEditCase`
            // permission. `data-edit-case-id` + `stopPropagation` (inside
            // `handleEditCaseClick`) mirror the pencil and prevent the row's
            // own click handler (which navigates into the case) from also
            // firing. Hover state turns the label green to match the pencil's
            // hover convention. Falls back to plain non-interactive text when
            // the user lacks edit permission.
            const caseId = info.row.original.caseId;
            if (!isCreateEditCase) {
              return (
                <div className="flex items-center gap-2">
                  <UserAvatar unassigned size={24} />
                  <span className="text-fletchers-gray-200 italic">Unassigned</span>
                </div>
              );
            }
            return (
              <button
                type="button"
                data-edit-case-id={caseId}
                onClick={handleEditCaseClick}
                className="group flex cursor-pointer items-center gap-2"
              >
                <UserAvatar unassigned size={24} />
                <span className="text-fletchers-gray-200 group-hover:text-fletchers-green-500 italic">Unassigned</span>
              </button>
            );
          }
          return (
            <div className="flex items-center gap-2">
              <UserAvatar name={userName} size={24} fontSize={10} />
              <span
                className={classNames('block max-w-[300px] overflow-hidden overflow-ellipsis whitespace-nowrap')}
                title={userName}
              >
                {userName}
              </span>
            </div>
          );
        },
        enableColumnFilter: true,
        enableSorting: false,
      },
      {
        accessorKey: 'dueDate',
        header: 'Due date',
        cell: (info) => (
          <>
            {info.getValue() ? (
              <span
                className={classNames('block max-w-[300px] overflow-hidden overflow-ellipsis whitespace-nowrap')}
                title={format(new Date(info.getValue() as string), 'dd MMM yyyy')}
              >
                {format(new Date(info.getValue() as string), 'dd MMM yyyy')}
              </span>
            ) : (
              ''
            )}
          </>
        ),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: '',
        id: 'actions',
        cell: (info) => {
          const rowValue = info.row.original;

          return (
            <div className="flex flex-nowrap items-center justify-end gap-2">
              {isCreateEditCase && (
                <button
                  type="button"
                  data-edit-case-id={rowValue.caseId}
                  onClick={handleEditCaseClick}
                  className="border-fletchers-gray-110 hover:bg-fletchers-gray-50 flex h-8 w-8 cursor-pointer items-center justify-center rounded border bg-white opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <PencilIcon className="[&>path]:fill-fletchers-black-900" />
                </button>
              )}
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    [handleEditCaseClick, isCreateEditCase],
  );

  const table = useReactTable({
    data: casesList,
    columns,
    getCoreRowModel: getCoreRowModel(),

    state: {
      pagination: casesPagination,
      sorting: casesSorting,
      columnFilters,
    },

    //** START PAGINATION */
    rowCount: casesRowCount,
    onPaginationChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        const newPaginationState = updaterOrValue(casesPagination);
        dispatch(setCasesPagination(newPaginationState));
      } else {
        dispatch(setCasesPagination(updaterOrValue));
      }
    },
    manualPagination: true,
    //** END PAGINATION */

    //** START SORTING */
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        const newSorting = updaterOrValue(casesSorting);
        dispatch(setCasesSorting(newSorting));
      } else {
        dispatch(setCasesSorting(updaterOrValue));
      }
      // Reset pagination to first page
      dispatch(setCasesPagination({ ...casesPagination, pageIndex: 0 }));
    },
    manualSorting: true,
    //** END SORTING */
  });

  return {
    table,
    filterOptions,
    selectedFilterOptions,
    isTableDataLoading: getCasesFetching,
    handleFilterChange,
  };
};
